import { DateRange } from '@adamftd/x-date-pickers-pro';
import {
  Box,
  Checkbox,
  Collapse,
  FormControlLabel,
  FormGroup,
  InputAdornment,
  InputLabel,
  Stack,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { Dayjs } from 'dayjs';
import { FieldArray, useFormik } from 'formik';
import { filter, includes, indexOf, keys, lowerCase, map, startCase } from 'lodash';
import { ReactNode, useMemo, useState } from 'react';
import { ChevronDown, ChevronUp } from 'react-feather';
import ButtonWithLoading from 'src/components/common/ButtonWithLoading';
import FormikForm from 'src/components/common/FormikForm';
import IconFeather from 'src/components/common/Icon';
import LoadingSkeleton from 'src/components/common/LoadingSkeleton';
import DateRangePickerWithDefaultIcon from 'src/components/DateRangePicker';
import { CreditCoinIcon } from 'src/components/Icons';
import ModalComponent from 'src/components/Modal/ModalComponent';
import { useQueryPlanFeatures } from 'src/services/queries';
import { FORMAT_DATE_TIME } from 'src/utils/constants/config';
import { LIST_ACTIONS } from 'src/utils/constants/featuresPoint';
import { getArrayDefault } from 'src/utils/helpers/arrayHelpers';
import { getDatePickerValue, handleDatePickerValueChange } from 'src/utils/helpers/dayjs';
import { getNumberOrDefault } from 'src/utils/helpers/number';
import { getStringOrDefault } from 'src/utils/helpers/stringHelpers';
import { TypePlanCategory, TypePlanFeature } from 'src/utils/typings/response/payment';

const PopupFilter = ({
  open,
  handleClose,
  onFilter,
  params,
}: {
  handleClose: () => void;
  open: boolean;
  onFilter: (payload: Record<string, any>) => void;
  params: Record<string, any>;
}) => {
  const {
    data: { data },
    isFetching,
  } = useQueryPlanFeatures();

  const formik = useFormik({
    initialValues: {
      spent_date_from: getStringOrDefault(params.spent_date_from),
      spent_date_to: getStringOrDefault(params.spent_date_to),
      credit_from: getNumberOrDefault(params.credit_from),
      credit_to: getNumberOrDefault(params.credit_to, 100),
      actions: getStringOrDefault(params.actions),
      feature_type_names: getArrayDefault(params.feature_type_names),
    },
    enableReinitialize: true,
    onSubmit(values, formikHelpers) {
      onFilter(values);
      handleClose();
    },
  });

  const filterExternalFeatures = useMemo(() => {
    return filter(data, (feature) => feature.is_internal);
  }, [data]);

  const { setFieldValue, values, handleChange } = formik;

  const handleChangeDateRangePicker = (value: DateRange<Dayjs>, fromTimeKey: string, toTimeKey: string) => {
    if (!value[0] || !value[1]) {
      setFieldValue(fromTimeKey, null);
      setFieldValue(toTimeKey, null);
    }

    const fromTime = handleDatePickerValueChange(FORMAT_DATE_TIME.DD_MM_YYYY, value[0]);
    const toTime = handleDatePickerValueChange(FORMAT_DATE_TIME.DD_MM_YYYY, value[1]);

    setFieldValue(fromTimeKey, fromTime);
    setFieldValue(toTimeKey, toTime);
  };

  const handleChangeAllFeatures = (checked: boolean, features?: Array<TypePlanFeature>) => {
    if (checked) {
      const featureMapped = map(features, ({ feature_type_name }) => feature_type_name);
      setFieldValue('feature_type_names', [...values.feature_type_names, ...featureMapped]);
    } else {
      const featureMapped = map(features, ({ feature_type_name }) => feature_type_name);
      const featureTypeNames = filter(values.feature_type_names, (feature) => !includes(featureMapped, feature));
      setFieldValue('feature_type_names', featureTypeNames);
    }
  };

  return (
    <ModalComponent styleBox={{ width: 600 }} open={open} handleClose={handleClose}>
      <FormikForm formik={formik}>
        <Stack spacing={2}>
          <Box textAlign={'center'}>
            <Typography variant="h5">Credit Usage Filter</Typography>
            <Typography variant="body2" color={'text.secondary'}>
              Select one or more of the criteria below.
            </Typography>
          </Box>

          <DateRangePickerWithDefaultIcon
            value={[
              getDatePickerValue(FORMAT_DATE_TIME.DD_MM_YYYY, values.spent_date_from),
              getDatePickerValue(FORMAT_DATE_TIME.DD_MM_YYYY, values.spent_date_to),
            ]}
            onChange={(value) => handleChangeDateRangePicker(value, 'spent_date_from', 'spent_date_to')}
            disableFuture
            localeText={{
              start: 'Spending date from',
              end: 'To',
            }}
            format={FORMAT_DATE_TIME.DD_MM_YYYY}
            slotProps={{
              textField: {
                size: 'small',
              },
            }}
          />

          <Stack alignItems={'center'} direction={'row'} spacing={2}>
            <TextField
              type="number"
              value={values.credit_from}
              id="credit_from"
              name="credit_from"
              inputProps={{ min: 0 }}
              onChange={handleChange}
              label="Credit From"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <CreditCoinIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
              fullWidth
              size="small"
            />
            <span>-</span>
            <TextField
              inputProps={{ min: 0 }}
              id="credit_to"
              name="credit_to"
              value={values.credit_to}
              onChange={handleChange}
              label="To"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <CreditCoinIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
              fullWidth
              size="small"
              type="number"
            />
          </Stack>

          <Box>
            <InputLabel shrink>Activity</InputLabel>
            <FormGroup row>
              <FieldArray
                name="actions"
                render={({ push, remove }) => {
                  return keys(LIST_ACTIONS).map((key) => (
                    <FormControlLabel
                      sx={{ ml: -0.5 }}
                      key={lowerCase(key)}
                      slotProps={{
                        typography: {
                          color: 'text.secondary',
                          variant: 'caption2',
                        },
                      }}
                      label={startCase(lowerCase(key))}
                      control={
                        <Checkbox
                          checked={values.actions.includes(lowerCase(key))}
                          onChange={(e) => {
                            if (e.target.checked) {
                              push(lowerCase(key));
                            } else {
                              const idx = indexOf(values.actions, lowerCase(key));
                              remove(idx);
                            }
                          }}
                          size="small"
                        />
                      }
                    />
                  ));
                }}
              />
            </FormGroup>
          </Box>

          {!isFetching ? (
            map(filterExternalFeatures, ({ category_name, features }: TypePlanCategory) => (
              <Dropdown title={category_name}>
                <FormGroup row>
                  <Stack spacing={1}>
                    <FormControlLabel
                      sx={{ ml: -0.5 }}
                      slotProps={{
                        typography: {
                          color: 'text.secondary',
                          variant: 'caption2',
                        },
                      }}
                      label={'All Features'}
                      control={
                        <Checkbox onChange={(_, checked) => handleChangeAllFeatures(checked, features)} size="small" />
                      }
                    />
                    <FieldArray
                      name="feature_type_names"
                      render={({ push, remove }) => {
                        return map(features, ({ feature_type_id, name, feature_type_name }) => {
                          const isChecked = includes(values.feature_type_names, feature_type_name);
                          return (
                            <FormControlLabel
                              sx={{ ml: -0.5 }}
                              key={feature_type_id}
                              slotProps={{
                                typography: {
                                  color: 'text.secondary',
                                  variant: 'caption2',
                                },
                              }}
                              label={startCase(name)}
                              control={
                                <Checkbox
                                  checked={isChecked}
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      push(feature_type_name);
                                    } else {
                                      const idx = indexOf(values.feature_type_names, feature_type_name);
                                      remove(idx);
                                    }
                                  }}
                                  size="small"
                                />
                              }
                            />
                          );
                        });
                      }}
                    />
                  </Stack>
                </FormGroup>
              </Dropdown>
            ))
          ) : (
            <LoadingSkeleton height={40} count={3} />
          )}

          <ButtonWithLoading type="submit" isRounded variant="contained" sx={{ height: 50 }}>
            Filter Now
          </ButtonWithLoading>
        </Stack>
      </FormikForm>
    </ModalComponent>
  );
};

export default PopupFilter;

const Dropdown = ({ title, children }: { title?: string; children: ReactNode }) => {
  const [isExpand, setIsExpand] = useState(false);
  const {
    palette: { background },
    shape: { borderRadius },
  } = useTheme();
  return (
    <Box sx={{ cursor: 'pointer' }}>
      <Typography
        sx={{
          p: 1.5,
          background: background.paper1,
        }}
        component={'span'}
        onClick={() => setIsExpand(!isExpand)}
        display={'flex'}
        alignItems={'center'}
        width={'100%'}
        color={'text.secondary'}
        variant="caption2"
      >
        {/* <Checkbox onChange={(_, e) => alert(e)} sx={{ pl: 0 }} checked={isExpand} size="small" /> */}
        {title}
        <Box flex={1} />
        <IconFeather muiProps={{ fontSize: 'small' }} icon={isExpand ? <ChevronUp /> : <ChevronDown />} />
      </Typography>

      <Collapse in={isExpand} unmountOnExit>
        <Box mt={1} pl={2.5}>
          {children}
        </Box>
      </Collapse>
    </Box>
  );
};
